import type { ListQuery, PaginatedResponse } from '@portals-platform/schemas';
import { keepPreviousData, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { useApiClient } from '../ApiClientProvider.js';
import { toSearchParams } from '../list-params.js';

/**
 * Query keys for list endpoints.
 *
 * Callers previously hand-built key arrays, so two components querying the same resource
 * could disagree on the key and never share a cache entry. Build keys through here.
 */
export const listKeys = {
  all: (resource: string) => [resource] as const,
  list: (resource: string, query: ListQuery) => [resource, 'list', query] as const,
  detail: (resource: string, id: string) => [resource, 'detail', id] as const,
};

export interface UseListQueryOptions<TItem>
  extends Omit<UseQueryOptions<PaginatedResponse<TItem>, Error>, 'queryKey' | 'queryFn'> {
  /** Cache key prefix, usually the resource name. Defaults to the path. */
  resource?: string;
}

/**
 * A paginated, sortable, filterable GET backed by the shared axios instance.
 *
 * `placeholderData: keepPreviousData` is the point: without it, paging or re-sorting
 * unmounts the table body for the duration of the request, so the page height collapses
 * and the scroll position jumps on every interaction.
 */
export function useListQuery<TItem>(
  path: string,
  query: ListQuery,
  { resource, ...options }: UseListQueryOptions<TItem> = {},
) {
  const client = useApiClient();
  const key = listKeys.list(resource ?? path, query);

  return useQuery<PaginatedResponse<TItem>, Error>({
    queryKey: key,
    queryFn: async () => {
      const response = await client.get<PaginatedResponse<TItem>>(path, {
        params: toSearchParams(query),
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
    ...options,
  });
}
